/**
 * Single source of truth for every word and link on the Den marketing site.
 * Copy is drawn from the product docs (docs.mls.onchainden.com). Edit here to
 * update content everywhere, sections only handle layout and styling.
 */

import type { ReactElement } from 'react';
import { blogCategories } from './blog-categories';

export const links = {
  // The two Den apps. The "Open app" CTAs open a chooser dialog (OpenAppDialog)
  // rather than linking directly; MLS (multi-layer security) is the promoted
  // product, Multisig is the legacy Safe-based app.
  mlsApp: 'https://mls.onchainden.com',
  multisigApp: 'https://app.onchainden.com',
  demo: 'mailto:hello@onchainden.com?subject=Request%20a%20Den%20demo',
  docs: 'https://docs.mls.onchainden.com',
  apiReference: 'https://docs.mls.onchainden.com/api-reference/introduction',
  contracts: 'https://docs.mls.onchainden.com/smart-contracts/overview',
  security:
    'https://docs.mls.onchainden.com/enterprise-security/security-layers',
  github: 'https://github.com/OnChainDen',
  x: 'https://x.com/OnChainDen',
  linkedin: 'https://www.linkedin.com/company/onchainden/',
} as const;

export type NavLink = {
  label: string;
  description: string;
  href: string;
  icon: string;
  external?: boolean;
};

export type NavMenu = { label: string; tagline?: string; items: NavLink[] };
type FaqAnswer = string | ReactElement;

export const productMenu: NavLink[] = [
  {
    label: 'Dashboard',
    description:
      'Manage your organization and assets from one unified dashboard.',
    href: '/product/dashboard-command-center',
    icon: 'platform',
  },
  {
    label: 'API & SDK',
    description: 'Automate your onchain operations and issue wallets in code.',
    href: '/product/api-sdk',
    icon: 'api',
  },
  {
    label: 'Mobile signing',
    description: 'Approve transactions from your phone, signed on-device.',
    href: '/product/mobile-signing',
    icon: 'device',
  },
  {
    label: 'Policy engine',
    description:
      'Govern every transaction with programmable approvals, scope, and limits.',
    href: '/product/policy-engine',
    icon: 'policy',
  },
  {
    label: 'Wallet-as-a-Service',
    description:
      'Issue secure customer wallets, embedded directly in your product.',
    href: '/product/wallet-as-a-service',
    icon: 'waas',
  },
  {
    label: 'No blockchain complexity',
    description:
      'Den sponsors gas and hides chain complexity, so transactions just work.',
    href: '/product/no-blockchain-complexity',
    icon: 'sparkles',
  },
  {
    label: 'Built for enterprise',
    description: 'Audit logs, flexible deployment, and dedicated support.',
    href: '/product/enterprise',
    icon: 'enterprise',
  },
];

export const securityMenu: NavLink[] = [
  {
    label: 'Multi-layer security',
    description: 'Three independent layers protect every transaction.',
    href: '/security/multi-layer-security',
    icon: 'shield',
  },
  {
    label: 'Onchain enforcement',
    description: 'Audited contracts re-check every transaction on-chain.',
    href: '/security/onchain-enforcement',
    icon: 'lock',
  },
  {
    label: 'Cloud-compromise immunity',
    description: "Your assets stay safe even if Den's servers are breached.",
    href: '/security/cloud-compromise-immunity',
    icon: 'cloudoff',
  },
  {
    label: 'Smart contract audits',
    description:
      'Independently audited by leading security firms Certora and yAudit.',
    href: '/security/smart-contract-audits',
    icon: 'verified',
  },
  {
    label: 'SOC 2 Type II',
    description:
      'Independently certified controls, third-party audits, and round-the-clock monitoring.',
    href: '/security/soc2',
    icon: 'cert',
  },
  {
    label: 'Open & verifiable',
    description: 'Open-source contracts you can read and verify yourself.',
    href: '/security/open-source',
    icon: 'opensource',
  },
];

export const solutionsMenu: NavLink[] = [
  {
    label: 'Treasury',
    description: 'Hold and govern corporate digital assets.',
    href: '/solutions/treasury',
    icon: 'treasury',
  },
  {
    label: 'Payments',
    description: 'Run high-volume, policy-gated payment flows.',
    href: '/solutions/payments',
    icon: 'payments',
  },
  {
    label: 'Protocol operations',
    description: 'Run contract calls and onchain strategies, safely.',
    href: '/solutions/onchain-operations',
    icon: 'defi',
  },
  {
    label: 'Wallet-as-a-Service',
    description: 'Provide secure wallets to your own customers.',
    href: '/product/wallet-as-a-service',
    icon: 'waas',
  },
  {
    label: 'Tokenization',
    description: 'Issue stablecoins and RWAs.',
    href: '/solutions/tokenization',
    icon: 'coins',
  },
];

export const developersMenu: NavLink[] = [
  {
    label: 'Documentation',
    description: 'Guides, core concepts, and how it works.',
    href: links.docs,
    icon: 'docs',
    external: true,
  },
  {
    label: 'API & SDK reference',
    description: 'Create wallets, execute transactions, and go live.',
    href: links.apiReference,
    icon: 'api',
    external: true,
  },
  {
    label: 'Smart contracts',
    description: 'Open-source, audited onchain contracts.',
    href: links.contracts,
    icon: 'contracts',
    external: true,
  },
  {
    label: 'GitHub',
    description: 'Read and verify the source yourself.',
    href: links.github,
    icon: 'github',
    external: true,
  },
];

export const blogMenu: NavLink[] = [
  {
    label: 'All articles',
    description: 'Insights and updates from Den.',
    href: '/blog',
    icon: 'docs',
  },
  ...blogCategories.map((category) => ({
    label: category.label,
    description: category.description,
    href: category.href,
    icon:
      category.slug === 'comparisons'
        ? 'payments'
        : category.slug === 'guides'
          ? 'docs'
          : 'waas',
  })),
];

export const navMenus: NavMenu[] = [
  {
    label: 'Product',
    tagline:
      'The complete wallet platform for internal operations and Wallet-as-a-Service.',
    items: productMenu,
  },
  {
    label: 'Solutions',
    tagline:
      'How teams put Den to work, from small startups to large enterprises.',
    items: solutionsMenu,
  },
  {
    label: 'Security',
    tagline: 'Defense-in-depth security built for enterprise and institutions.',
    items: securityMenu,
  },
  {
    label: 'Developers',
    tagline:
      'Everything you need to build on Den, from automating your operations to providing wallets directly in your product.',
    items: developersMenu,
  },
  {
    label: 'Blog',
    tagline:
      'Practical guides and thoughtful comparisons for your next onchain move.',
    items: blogMenu,
  },
];

export const hero = {
  badges: ['SOC 2 Type II', 'Multiple 3rd-party Security Audits'],
  subtitle:
    'Manage your treasury, run internal operations, and launch digital asset products using wallets that protect against the most sophisticated hackers.',
  primaryCta: 'Book a demo',
  secondaryCta: 'Open the app',
} as const;

/**
 * Customer-logo trust strip beneath the hero. Logos live in public/logos and are
 * rendered as single-color silhouettes (CSS mask) so they all share one color —
 * edit that color in one place via TrustStrip's `text-*` class.
 *
 * Two per-logo knobs:
 *  • `ratio` — the asset's true width/height aspect (files are tightly cropped),
 *    so the logo fills the base height. Set this to width÷height when swapping a
 *    logo; don't use it to resize.
 *  • `scale` — size multiplier (1 = base height). Bump up/down to optically
 *    balance logos that read larger or smaller than the rest (e.g. 0.85 to
 *    shrink, 1.15 to grow). Aspect is preserved.
 */
export const trustStrip = {
  title: 'Trusted by leading teams',
  logos: [
    { name: 'Juicebox', src: '/logos/juicebox.png', ratio: 4.79, scale: 1 },
    { name: 'Hop', src: '/logos/hop.svg', ratio: 2.65, scale: 1 },
    { name: 'Sky Mavis', src: '/logos/skymavis.svg', ratio: 3.98, scale: 1.2 },
    { name: 'Lido', src: '/logos/lido.png', ratio: 4.13, scale: 1 },
    { name: 'Worldcoin', src: '/logos/worldcoin.svg', ratio: 5.87, scale: 1 },
    { name: 'Alchemix', src: '/logos/alchemix.svg', ratio: 4.23, scale: 1.25 },
    { name: 'PleasrDAO', src: '/logos/pleasrdao.svg', ratio: 6.07, scale: 0.7 },
    {
      name: 'Index Coop',
      src: '/logos/index-coop.png',
      ratio: 4.44,
      scale: 0.8,
    },
    { name: 'Olympus', src: '/logos/olympus.svg', ratio: 5.35, scale: 0.9 },
    { name: 'Redacted', src: '/logos/redacted.png', ratio: 7.1, scale: 0.7 },
  ],
} as const;

/** Expanded homepage stories for the capabilities introduced in the bento. */
export const capabilityDetails = {
  eyebrow: 'operational excellence',
  title: 'Everything your team needs to operate onchain with confidence.',
  description:
    'From day-to-day treasury work to customer-facing products, Den keeps every workflow fast, legible, and governed.',
  items: [
    {
      id: 'treasury',
      capability: 'Manage your treasury',
      title: 'Hold, move, and deploy digital assets without giving up control.',
      points: [
        {
          title: 'One portfolio across chains',
          description:
            'See balances and activity across every wallet and network in one place.',
        },
        {
          title: 'Onchain operations',
          description:
            'Deploy assets into protocols through the same governed workflow your team already uses.',
        },
        {
          title: 'Live operational context',
          description:
            'Understand what moved, who initiated it, and which policy approved it without stitching together explorers.',
        },
      ],
    },
    {
      id: 'payments',
      capability: 'Streamline payments',
      title:
        'Delegate payments with policies that keep every transaction within your limits.',
      points: [
        {
          title: 'Batch payments',
          description:
            'Prepare vendor payments, payroll, and distributions as one clear reviewable batch.',
        },
        {
          title: 'Policy-gated transactions',
          description:
            'Apply spending limits, destinations, and approval rules before funds can move.',
        },
        {
          title: 'Configure approvals',
          description:
            'Give operators and signers the permissions they need without handing everyone unrestricted access.',
        },
      ],
    },
    {
      id: 'issuance',
      capability: 'Issue stablecoins & RWAs',
      title:
        'Manage issuance, governance, and liquidity from one secure control plane.',
      points: [
        {
          title: 'Programmatic issuance',
          description:
            'Coordinate minting, burning, and treasury flows through the dashboard or API.',
        },
        {
          title: 'Controls built into the workflow',
          description:
            'Require the right compliance, governance, and liquidity checks for every action.',
        },
        {
          title: 'Operational visibility',
          description:
            'Keep supply, holders, requests, and approvals legible to the teams responsible for them.',
        },
      ],
    },
    {
      id: 'products',
      capability: 'Launch digital asset products',
      title:
        'Give every customer a secure wallet without building custody from scratch.',
      points: [
        {
          title: 'Embedded wallets',
          description:
            'Create wallets inside your own product while Den handles the security-critical infrastructure.',
        },
        {
          title: 'Dashboard, API, and SDK',
          description:
            'Start with a polished interface, automate in code, or combine both as your product grows.',
        },
        {
          title: 'Built for many product models',
          description:
            'Power stablecoin apps, tokenized assets, marketplaces, AI agents, and more from one platform.',
        },
      ],
    },
    {
      id: 'chain',
      capability: 'Make the chain invisible',
      title:
        'Hide gas, addresses, and routing so teams can focus on the transaction.',
      points: [
        {
          title: 'Gasless operations',
          description:
            'Let users send transactions without maintaining fee balances on every network.',
        },
        {
          title: 'Human-readable review',
          description:
            'Replace opaque calldata and raw addresses with the action, destination, and impact that matter.',
        },
        {
          title: 'Automatic routing',
          description:
            'Move across supported networks through a familiar flow instead of exposing chain complexity.',
        },
      ],
    },
    {
      id: 'policies',
      capability: 'Control transactions with policies',
      title:
        'Create rules to control which transactions can be executed and by whom.',
      points: [
        {
          title: 'Granular permissions',
          description:
            'Scope people, groups, and applications to only the transactions their role requires.',
        },
        {
          title: 'Independent enforcement',
          description:
            'Re-check policy on the signing device, in the Guardian, and again in the smart contract.',
        },
        {
          title: 'Fast paths for low risk',
          description:
            'Auto-approve routine operations while escalating high-value or unusual actions to the right people.',
        },
      ],
    },
    {
      id: 'enterprise',
      capability: 'Ready for Enterprise',
      title:
        'Bring auditability, deployment control, and compliance to onchain operations.',
      points: [
        {
          title: 'Complete audit trail',
          description:
            'Keep an immutable record of policy changes, approvals, signers, and transaction activity.',
        },
        {
          title: 'Flexible deployment',
          description:
            'Run with Den Cloud, in your own cloud, or on-premise to meet security and residency needs.',
        },
        {
          title: 'Enterprise readiness',
          description:
            'Operate with SOC 2 Type II controls, independent audits, and support for complex organizations.',
        },
      ],
    },
  ],
} as const;

export const faqs: Array<{ q: string; a: FaqAnswer }> = [
  {
    q: 'What is Den’s Multi-Layer Security Wallet?',
    a: 'Den is a digital asset wallet platform for treasury operations, payments, smart contract interactions, and Wallet-as-a-Service. In the standard transaction flow, policies are independently checked by the signing client, the off-chain Guardian, and onchain smart contracts.',
  },
  {
    q: 'How is Den different from MPC wallets?',
    a: 'Multi-party computation (MPC) distributes signing across key shares. Providers can add their own approval policies and security controls. Den’s Multi-Layer Security Wallet focuses on independently validating transaction policies in the signing client, the Guardian, and onchain smart contracts. When comparing providers, ask where policies are enforced and which controls remain effective if a component is compromised.',
  },
  {
    q: 'How is Den different from a Safe multisig?',
    a: 'A multisig requires a threshold of owner signatures. Safe can extend that model with modules and guards for additional controls. Den brings configurable transaction policies, mobile and API signing, a Guardian, and onchain enforcement into an integrated operational workflow. Compare the complete configuration, including enabled modules, signers, and recovery permissions.',
  },
  {
    q: 'Can we embed Den wallets in our own product?',
    a: 'Yes. Den’s Wallet-as-a-Service lets you create and manage customer wallets through the API and SDK. Define transaction permissions, automate operations with API members, and combine programmatic workflows with approvals from your team.',
  },
  {
    q: 'Which blockchain networks does Den support?',
    a: 'Den supports EVM networks. The deployments page in the smart contract documentation lists the supported networks and deployed contract addresses; check it for the current list when planning an integration.',
  },
  {
    q: 'How does Den handle blockchain transaction fees?',
    a: 'Den sponsors network fees so teams do not need to maintain gas balances for each account. Plans include a fee allowance, with usage beyond that allowance billed as overages.',
  },
  {
    q: 'Can we deploy in our own cloud or on-premise?',
    a: 'Yes. Den offers three deployment models. Den Cloud is fully managed, Your Cloud runs in your own AWS, GCP, or Azure for data residency control, and On-Premise runs in your own data center. Every option includes forward-deployed engineering support.',
  },
  {
    q: 'Are the smart contracts audited and open-source?',
    a: 'Yes. The contracts are open-source on GitHub and have undergone independent third-party audits by Certora and yAudit. Anyone can read the code and verify the deployed contracts against the source.',
  },
  {
    q: 'How do approvals and policies work?',
    a: 'Each transaction is governed by a policy that defines who can approve it, which transactions are allowed, and any spending or time limits. Policies can auto-approve low-risk operations or require M-of-N approval from specific members or groups, with sign-off from the mobile app or programmatically via the API.',
  },
  {
    q: 'Who is Den built for?',
    a: 'Den supports organizations as they scale. The platform is designed to support large enterprises with sophisticated security needs, as well as early-stage startups that need fast flexibility. Den is used for internal treasury, payments and protocol operations, as well as companies issuing secure wallets to end customers.',
  },
];

export const finalCta = {
  title: 'Move onchain with confidence.',
  subtitle:
    'Explore enterprise crypto wallets with programmable policies, mobile approvals, and onchain enforcement.',
};

export const footer: {
  tagline: string;
  columns: NavMenu[];
} = {
  tagline:
    'Enterprise crypto wallets for treasury, stablecoin payments, and Wallet-as-a-Service, with multi-layer security.',
  columns: [
    { label: 'Product', items: productMenu },
    { label: 'Security', items: securityMenu },
    { label: 'Solutions', items: solutionsMenu },
    { label: 'Developers', items: developersMenu },
    { label: 'Blog', items: blogMenu },
  ],
};
